import { ReactNode } from 'react';
import { useSelector } from 'react-redux';

import { useAuthentication } from '../../hooks/useAuthentication';
import { selectValidAccessToken } from '../../store/authentication';

type Props = {
    children: ReactNode;
};

/**
 * Keeps an already-started co-applicant session refreshed in the background (via `useAuthentication`) and
 * gates `children` behind a genuinely VALID token (`selectValidAccessToken`) -- nothing more. It deliberately
 * does NOT attempt to initiate a from-scratch login or consume an OAuth `?code&state` callback itself; both
 * current callers (`SubmitCoApplicantWizardDK`/`SubmitCoApplicantWizardNO`) already have their own dedicated
 * page for that (`CoApplicantEskatKreditStatusConsentPageDK`/`CoApplicantLogInPage`), driven from their own
 * `mitid-login`/SBL actions, reached via each wizard's own `!accessToken` gate -- this component is never
 * mounted until that page has already produced a token.
 *
 * Previously this also rendered `<Authenticator autoLogin={true}>` as a fallback for a genuinely absent token,
 * so it could work as a fully standalone, from-scratch login component for a hypothetical future caller
 * without an identical outer gate. Removed after a closer look (PR #524 review) found that fallback had its
 * own, unrelated bugs: `useAuthentication`'s own `!accessToken` effect and `Authenticator`/`AuthenticationWrapper`'s
 * `autoLogin` effect could BOTH independently call `initAuthentication()` on the same cold mount -- at best
 * two redundant `/init` requests, at worst a genuine OAuth callback landing here would have its `getAccessToken`
 * exchange silently discarded (by the very correlation guard meant to protect it) the moment the second,
 * redundant `initAuthentication()` call overwrote `authenticationState` first, followed by a real redirect
 * loop. Since neither current caller ever reaches this component with an absent token in the first place (see
 * below), that fallback was speculative code for a caller that doesn't exist -- simpler and safer to remove it
 * than to make it correct.
 *
 * The reason a raw-vs-valid distinction doesn't matter anymore: `useAuthentication` and
 * `Authenticator`/`AuthenticationWrapper` used to each independently decide what to do about auth on mount,
 * using different criteria (raw presence vs. real validity) and reacting concurrently with nothing coordinating
 * them. When a persisted token existed but had expired (e.g. this component remounts after a page
 * reload/backgrounded tab mid-session -- common on mobile during a multi-step KYC/signing flow), both effects
 * fired from the same mount: `useAuthentication` dispatched `refreshAccessToken()`, while `AuthenticationWrapper`
 * independently dispatched `initAuthentication()` (a real full-page redirect) -- at best an unwanted forced
 * re-login despite already being authenticated; at worst, `initAuthentication`'s `initiate` action synchronously
 * cleared `authenticationData` before awaiting anything, which could make a concurrently-succeeding refresh's
 * own stale-response guard compare against `undefined` and silently discard an otherwise-legitimate refresh.
 * Removing `Authenticator` from this component entirely removes the second decision-maker, not just reorders
 * it: `useAuthentication` is now the only thing here that ever decides what to do about the token, so there is
 * nothing left to race. Gating `children` on `selectValidAccessToken` (rather than raw presence) also means
 * protected pages -- and whatever they fetch on mount (e.g. `useApplicationStateRefresh`) -- never render with
 * a token already known to be expired while a refresh is still in flight, avoiding a possible spurious 401
 * during that window.
 */
const WithAuthentication = ({ children }: Props) => {
    useAuthentication();
    const accessToken = useSelector(selectValidAccessToken);
    if (!accessToken) {
        return null;
    }
    return <>{children}</>;
};

export default WithAuthentication;
